import React, { useState, useRef, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import axios from 'axios';

import { AvatarDisplay } from '@/components/avatar/AvatarDisplay';
import { AvatarCropDialog } from '@/components/avatar/AvatarCropDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const avatarOpts = { responseType: 'blob', withCredentials: true, validateStatus: () => true };

function getCroppedBlob(image, crop, circular) {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const w = crop.width * scaleX;
    const h = crop.height * scaleY;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (circular) {
      ctx.beginPath();
      ctx.arc(w / 2, h / 2, Math.min(w, h) / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
    }
    ctx.drawImage(image, crop.x * scaleX, crop.y * scaleY, w, h, 0, 0, w, h);
    canvas.toBlob((blob) => resolve(blob), circular ? 'image/png' : 'image/jpeg', 0.92);
  });
}

function validateFile(file) {
  if (!file) return null;
  if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return null; }
  if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return null; }
  return file;
}

export function MemberAvatar({ userId, name, size = 'md', editable = false, onUpload }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showCrop, setShowCrop] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [circularCrop, setCircularCrop] = useState(false);
  const [dragging, setDragging] = useState(false);
  const imgRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    axios.get(`${API}/members/avatar/${userId}`, avatarOpts).then(res => {
      if (cancelled) return;
      if (res.status === 200 && res.data && res.data.size > 0) {
        setBlobUrl(URL.createObjectURL(res.data));
      } else {
        setLoadFailed(true);
      }
    }).catch(() => {
      if (!cancelled) setLoadFailed(true);
    });
    return () => { cancelled = true; };
  }, [userId]);

  const onImageLoad = useCallback((e) => {
    imgRef.current = e.currentTarget;
    const { width, height } = e.currentTarget;
    const side = Math.min(width, height) * 0.85;
    const x = (width - side) / 2;
    const y = (height - side) / 2;
    setCrop({ unit: 'px', x, y, width: side, height: side });
    setCompletedCrop({ unit: 'px', x, y, width: side, height: side });
  }, []);

  const loadImageForCrop = (file) => {
    const validated = validateFile(file);
    if (!validated) return;
    const reader = new FileReader();
    reader.onload = () => { setImageSrc(reader.result); setShowCrop(true); };
    reader.readAsDataURL(validated);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) loadImageForCrop(file);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) loadImageForCrop(file);
  };

  const handleCropSave = async () => {
    if (!imgRef.current || !completedCrop) return;
    setUploading(true);
    try {
      const croppedBlob = await getCroppedBlob(imgRef.current, completedCrop, circularCrop);
      const ext = circularCrop ? 'png' : 'jpg';
      const file = new File([croppedBlob], `avatar.${ext}`, { type: circularCrop ? 'image/png' : 'image/jpeg' });
      await api.uploadAvatar(file);
      toast.success('Profile picture updated');
      const res = await axios.get(`${API}/members/avatar/${userId}`, {
        responseType: 'blob', withCredentials: true,
      });
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      setBlobUrl(URL.createObjectURL(res.data));
      setLoadFailed(false);
      setShowCrop(false);
      setImageSrc(null);
      if (onUpload) onUpload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload');
    }
    setUploading(false);
  };

  const handleCropCancel = () => {
    setShowCrop(false);
    setImageSrc(null);
    setCrop(null);
    setCompletedCrop(null);
  };

  return (
    <>
      <AvatarDisplay
        userId={userId} name={name} size={size}
        blobUrl={blobUrl} loadFailed={loadFailed}
        editable={editable} uploading={uploading} dragging={dragging}
        fileRef={fileRef} onFileChange={handleFileChange}
        onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
      />
      <AvatarCropDialog
        open={showCrop} onCancel={handleCropCancel}
        imageSrc={imageSrc} crop={crop} setCrop={setCrop}
        onComplete={(c) => setCompletedCrop(c)}
        circularCrop={circularCrop} setCircularCrop={setCircularCrop}
        onImageLoad={onImageLoad} uploading={uploading} onSave={handleCropSave}
        dragging={dragging} fileRef={fileRef}
        onDialogDragOver={handleDragOver} onDialogDragLeave={handleDragLeave} onDialogDrop={handleDrop}
      />
    </>
  );
}
